import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardShell from "@/components/DashboardShell";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import LessonPlayer from "@/pages/LessonPlayer";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import Resources from "@/pages/Resources";
import WebinarsPage from "@/pages/WebinarsPage";
import SneakPeek from "@/pages/SneakPeek";
import OnboardingQuiz from "@/pages/OnboardingQuiz";
import AICoach from "@/pages/AICoach";
import Account from "@/pages/Account";
import Shop from "@/pages/Shop";
import ShopSuccess from "@/pages/ShopSuccess";
import PaymentReturn from "@/pages/PaymentReturn";
import Quiz from "@/pages/Quiz";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Waitlist from "@/pages/Waitlist";
import Certificate from "@/pages/Certificate";
import Certificates from "@/pages/Certificates";
import Achievements from "@/pages/Achievements";
import Agreement from "@/pages/Agreement";
import JournalExport from "@/pages/JournalExport";
import AdminShell from "@/pages/admin/AdminShell";
import AdminCompliance from "@/pages/admin/AdminCompliance";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminWebinars from "@/pages/admin/AdminWebinars";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminOnboarding from "@/pages/admin/AdminOnboarding";
import AdminQuizzes from "@/pages/admin/AdminQuizzes";
import AdminMentorCalls from "@/pages/admin/AdminMentorCalls";
import AdminTradeSetups from "@/pages/admin/AdminTradeSetups";
import AdminChannelGuides from "@/pages/admin/AdminChannelGuides";
import AdminChannelMirrors from "@/pages/admin/AdminChannelMirrors";
import AdminRecaps from "@/pages/admin/AdminRecaps";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminSocialLinks from "@/pages/admin/AdminSocialLinks";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import AdminShop from "@/pages/admin/AdminShop";
import AdminResidencyWelcome from "@/pages/admin/AdminResidencyWelcome";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import ReferralActivationsWatcher from "@/components/ReferralActivationsWatcher";
import TradeHealth from "@/pages/TradeHealth";
import ResidencyInfo from "@/pages/ResidencyInfo";
import DiscordGuide from "@/pages/DiscordGuide";
import ResidencyApply from "@/pages/ResidencyApply";
import ResidencyOffer from "@/pages/ResidencyOffer";

function App() {
  useEffect(() => {
    document.title = "Peak Market Society — Elevate Your Success";
  }, []);

  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

  if (maintenanceMode) {
    return (
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <LanguageProvider>
            <Routes>
              <Route path="*" element={<Waitlist />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </LanguageProvider>
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <ScrollToTop />
            <ReferralActivationsWatcher />
            <ConditionalChrome>
              <Routes>
                <Route path="/waitlist" element={<Waitlist />} />
                <Route path="/certificates/:code" element={<Certificate />} />
                <Route path="/journal/export/:reportId" element={<JournalExport />} />

                <Route path="/" element={<Landing />} />
                <Route path="/preview" element={<SneakPeek />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/residency" element={<ResidencyInfo />} />
                <Route path="/discord-guide" element={<DiscordGuide />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/residency/apply" element={<ResidencyApply />} />
                <Route path="/login" element={<Login />} />
                <Route path="/onboarding" element={<OnboardingQuiz />} />
                <Route path="/register" element={<Navigate to="/onboarding" replace />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Signed-in members see the real pages via ProtectedRoute
                    below. Guests and Free tier are redirected inside each
                    page component to /preview so the funnel still works. */}
                <Route element={<SmartShell />}>
                  <Route path="/courses/:id" element={<CourseDetail />} />
                  <Route path="/articles/:id" element={<ArticleDetail />} />
                </Route>

                <Route element={<ProtectedRoute><DashboardShellWrapper /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/articles" element={<Articles />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/webinars" element={<WebinarsPage />} />
                  <Route path="/ai-coach" element={<AICoach />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/residency/offer/:appId" element={<ResidencyOffer />} />
                  <Route path="/shop/success" element={<ShopSuccess />} />
                  <Route path="/certificates" element={<Certificates />} />
                  <Route path="/achievements" element={<Achievements />} />
                  <Route path="/health-check" element={<TradeHealth />} />
                  <Route path="/playbooks" element={<Navigate to="/resources?tab=playbooks" replace />} />
                  <Route path="/analytics" element={<Navigate to="/resources?tab=analytics" replace />} />
                  <Route path="/lessons/:id" element={<LessonPlayer />} />
                  <Route path="/quizzes/:id" element={<Quiz />} />
                  <Route path="/checkout/agreement/:tier" element={<Agreement />} />
                  <Route path="/admin" element={<AdminShell />}>
                    <Route index element={<AdminCompliance />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="content" element={<AdminContent />} />
                    <Route path="applications" element={<AdminApplications />} />
                    <Route path="webinars" element={<AdminWebinars />} />
                    <Route path="security" element={<AdminSecurity />} />
                    <Route path="onboarding" element={<AdminOnboarding />} />
                    <Route path="quizzes" element={<AdminQuizzes />} />
                    <Route path="mentor-calls" element={<AdminMentorCalls />} />
                    <Route path="trade-setups" element={<AdminTradeSetups />} />
                    <Route path="channel-guides" element={<AdminChannelGuides />} />
                    <Route path="channel-mirrors" element={<AdminChannelMirrors />} />
                    <Route path="recaps" element={<AdminRecaps />} />
                    <Route path="weekly-recap" element={<Navigate to="/admin/recaps?tab=weekly" replace />} />
                    <Route path="monthly-recap" element={<Navigate to="/admin/recaps?tab=monthly" replace />} />
                    <Route path="residency-welcome" element={<AdminResidencyWelcome />} />
                    <Route path="promotions" element={<AdminPromotions />} />
                    <Route path="partners" element={<AdminPartners />} />
                    <Route path="social-links" element={<AdminSocialLinks />} />
                    <Route path="shop" element={<AdminShop />} />
                  </Route>
                </Route>

                <Route path="/payment-success" element={
                  <ProtectedRoute><PaymentReturn /></ProtectedRoute>
                } />
              </Routes>
            </ConditionalChrome>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

// Hide top nav/footer on immersive pages like /waitlist and public certificates
function ConditionalChrome({ children }) {
  const { pathname } = window.location;
  const bare = pathname === "/waitlist" || pathname.startsWith("/certificates/");
  if (bare) return children;
  return (
    <>
      <Navbar />
      <div className="flex-1">{children}</div>
      <Footer />
    </>
  );
}

function DashboardShellWrapper() {
  return <DashboardShell />;
}

// Renders the sidebar/shell for authenticated users on public routes
// (Courses, CourseDetail, Articles) so navigating in from a quiz completion
// or elsewhere doesn't drop the sidebar. Unauthenticated visitors keep the
// public marketing chrome.
function SmartShell() {
  const { user } = useAuth();
  return user ? <DashboardShell /> : <Outlet />;
}

export default App;
